import {FC, useState} from 'react'
import clsx from 'clsx'
import {useIntl} from 'react-intl'
import {ErrorMessage} from '../../../../app/modules/errors/formikMessages/errorMessages'
import {KTIcon} from '../KTIcon'

type FormInputProps = {
  type: string
  name: string
  placeholder: string
  formik: any
  disabled?: boolean
  isTextarea?: boolean
  height?: string
}

const FormInput: FC<FormInputProps> = ({
  type,
  name,
  placeholder,
  formik,
  disabled = false,
  isTextarea = false,
  height = '100px',
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const inputProps = formik.getFieldProps(name)
  const intl = useIntl()

  // Función para obtener el error de campos anidados en arrays
  const getNestedError = (fieldName: string) => {
    const parts = fieldName.match(/(\w+)\[(\d+)\]\.(\w+)/)
    if (parts) {
      const [, arrayName, index, fieldKey] = parts
      const arrayErrors = formik.errors[arrayName]
      if (Array.isArray(arrayErrors) && arrayErrors[parseInt(index)]) {
        return arrayErrors[parseInt(index)][fieldKey]
      }
    }
    return formik.errors[fieldName]
  }

  // Función para verificar si fue tocado un campo anidado
  const getNestedTouched = (fieldName: string) => {
    const parts = fieldName.match(/(\w+)\[(\d+)\]\.(\w+)/)
    if (parts) {
      const [, arrayName, index, fieldKey] = parts
      const arrayTouched = formik.touched[arrayName]
      if (Array.isArray(arrayTouched) && arrayTouched[parseInt(index)]) {
        return arrayTouched[parseInt(index)][fieldKey]
      }
    }
    return formik.touched[fieldName]
  }

  const isPassword = type === 'password'
  const inputType = isPassword && showPassword ? 'text' : type

  const isTouched = getNestedTouched(name)
  const error = getNestedError(name)

  const isValid = isTouched && !error
  const isInvalid = isTouched && error

  return (
    <div className='form-floating col-12 my-3 px-1 position-relative'>
      {isTextarea ? (
        <textarea
          placeholder={intl.formatMessage({id: placeholder})}
          {...inputProps}
          name={name}
          disabled={disabled}
          className={clsx(
            'form-control form-control-solid',
            {'is-invalid': isInvalid},
            {'is-valid': isValid}
          )}
          style={{
            overflow: 'auto',
            height: height,
          }}
          rows={5}
        />
      ) : (
        <>
          <input
            placeholder={intl.formatMessage({id: placeholder})}
            {...inputProps}
            type={inputType}
            name={name}
            disabled={disabled}
            className={clsx(
              'form-control form-control-solid pe-10',
              {'is-invalid': isInvalid},
              {'is-valid': isValid}
            )}
            autoComplete={isPassword ? 'new-password' : 'off'}
            list='emptyDatalist'
          />

          {isPassword && <datalist id='emptyDatalist'></datalist>}

          {/* 👁️ Botón para mostrar/ocultar contraseña */}
          {isPassword && (
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='btn position-absolute p-0 border-0 bg-transparent end-0 me-10'
              style={{
                top: '35%',
                transform: 'translateY(-50%)',
                pointerEvents: 'auto',
              }}
              tabIndex={-1}
            >
              {showPassword ? (
                <KTIcon iconName='eye-slash' className='fs-1 text-gray-500' />
              ) : (
                <KTIcon iconName='eye' className='fs-1 text-gray-500' />
              )}
            </button>
          )}
        </>
      )}

      <label htmlFor={name}>{intl.formatMessage({id: placeholder})}</label>

      <div style={{minHeight: '24px'}}>
        {isInvalid && <ErrorMessage errorMessage={intl.formatMessage({id: error})} />}
      </div>
    </div>
  )
}

export default FormInput
